import React, { useState } from 'react';
import { FaTimes, FaRobot, FaCheckCircle, FaPrint, FaDownload, FaExclamationTriangle, FaPause, FaUndo } from 'react-icons/fa';

const TaperModal = ({ medication, patientData, taperPlan, onClose }) => {
  const [isAiGenerated] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-overlay">
      <div className="bg-neo-card rounded-lg border-2 border-neo-main shadow-neo-brutal max-w-4xl w-full max-h-[90vh] overflow-hidden modal-content flex flex-col">
        {/* Header */}
        <div className="bg-neo-accent text-white p-6 border-b-2 border-neo-main flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white text-neo-main rounded-md flex items-center justify-center border-2 border-neo-main shadow-neo-sm">
              <FaRobot className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-black flex items-center gap-3">
                Tapering Schedule
                {isAiGenerated && (
                  <span className="text-xs bg-neo-warning text-neo-main px-3 py-1 rounded-md border-2 border-neo-main font-bold shadow-neo-sm">
                    AI-Personalized
                  </span>
                )}
              </h2>
              <p className="text-white font-bold mt-1 opacity-90">{medication.name}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 bg-white text-neo-main hover:bg-neo-bg rounded-md flex items-center justify-center 
                     transition-all duration-200 border-2 border-neo-main shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">

          {taperPlan && (
            <div className="space-y-6 animate-fade-in">
              {isAiGenerated && (
                <div className="bg-neo-bg border-2 border-neo-main p-5 rounded-lg shadow-neo-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white rounded-md border-2 border-neo-main
                                  flex items-center justify-center shadow-neo-sm flex-shrink-0">
                      <FaRobot className="text-neo-accent text-xl" />
                    </div>
                    <div>
                      <h4 className="font-black text-neo-main mb-1">AI-Generated Personalized Schedule</h4>
                      <p className="text-sm text-neo-text font-medium">
                        This tapering schedule considers your patient's age ({patientData.age}), 
                        frailty (CFS {patientData.cfs_score || 'N/A'}), and specific comorbidities. 
                        All recommendations follow evidence-based clinical protocols.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black text-neo-main mb-4 border-b-2 border-neo-main inline-block">Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Drug Class', value: taperPlan.drug_class },
                    { label: 'Risk Profile', value: taperPlan.risk_profile },
                    { label: 'Strategy', value: taperPlan.taper_strategy },
                    { label: 'Duration', value: `${taperPlan.total_duration_weeks} weeks` },
                  ].map((item, i) => (
                    <div key={i} className="bg-neo-card p-4 rounded-md border-2 border-neo-main shadow-neo-sm">
                      <p className="text-xs text-neo-text-sub font-bold uppercase tracking-wide mb-1">{item.label}</p>
                      <p className="font-black text-neo-main">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-black text-neo-main mb-4 flex items-center gap-2">
                  <FaCheckCircle className="text-neo-success" />
                  Week-by-Week Schedule
                </h3>
                <div className="space-y-4">
                  {taperPlan.steps.map((step, index) => (
                    <div 
                      key={index} 
                      className="bg-white border-2 border-neo-main rounded-lg p-5 shadow-neo-brutal 
                               hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-300 animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-4">
                            <span className="bg-neo-accent text-white border-2 border-neo-main
                                           px-4 py-1.5 rounded-md text-sm font-bold shadow-neo-sm">
                              Week {step.week}
                            </span>
                            <span className="text-xl font-black text-neo-main">{step.dose}</span>
                            <span className="text-sm text-neo-text font-bold bg-neo-card border-2 border-neo-main px-3 py-1 rounded-md">
                              {step.percentage_of_original}% of original
                            </span>
                          </div>

                          <div className="space-y-3">
                            <div className="bg-neo-bg/30 p-4 rounded-md border-2 border-neo-main">
                              <p className="text-sm font-black text-neo-main mb-1">Instructions:</p>
                              <p className="text-neo-text font-medium">{step.instructions}</p>
                            </div>

                            <div className="bg-neo-card p-4 rounded-md border-2 border-neo-main">
                              <p className="text-sm font-black text-neo-accent mb-1">Monitoring:</p>
                              <p className="text-neo-text font-medium text-sm">{step.monitoring}</p>
                            </div>

                            {step.withdrawal_symptoms_to_watch?.length > 0 && (
                              <div>
                                <p className="text-sm font-black text-neo-main mb-2">Watch for:</p>
                                <div className="flex flex-wrap gap-2">
                                  {step.withdrawal_symptoms_to_watch.map((symptom, idx) => (
                                    <span 
                                      key={idx} 
                                      className="text-xs px-3 py-1.5 bg-neo-warning text-neo-main 
                                               rounded-md border-2 border-neo-main font-bold shadow-neo-sm"
                                    >
                                      {symptom}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-center">
                          <div className="relative w-16 h-16">
                            <svg className="w-full h-full transform -rotate-90">
                              <circle cx="32" cy="32" r="28" stroke="#000000" strokeWidth="4" fill="none" className="opacity-10" />
                              <circle
                                cx="32" cy="32" r="28"
                                stroke="currentColor"
                                className="text-neo-accent"
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray={`${(100 - step.percentage_of_original) * 1.76} 176`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-sm font-black text-neo-main">
                                {100 - step.percentage_of_original}%
                              </span>
                            </div>
                          </div>
                          <span className="text-xs text-neo-text-sub font-bold mt-1">Reduced</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {taperPlan.success_indicators?.length > 0 && (
                <div className="bg-neo-success text-neo-main p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                  <h3 className="text-lg font-black mb-3 flex items-center gap-2">
                    <FaCheckCircle className="text-white drop-shadow-sm" />
                    Success Indicators
                  </h3>
                  <ul className="space-y-2">
                    {taperPlan.success_indicators.map((indicator, index) => (
                      <li key={index} className="flex items-start font-bold gap-2">
                        <span className="mt-1 bg-white text-neo-success rounded-full w-4 h-4 flex items-center justify-center text-xs border border-neo-main">✓</span>
                        <span>{indicator}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-neo-warning text-neo-main p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black mb-3 flex items-center gap-2">
                  <FaPause className="text-white drop-shadow-sm" />
                  When to PAUSE Tapering
                </h3>
                <ul className="space-y-2">
                  {taperPlan.pause_criteria.map((criteria, index) => (
                    <li key={index} className="flex items-start font-bold gap-2">
                      <FaExclamationTriangle className="mt-1 flex-shrink-0 text-white drop-shadow-sm" />
                      <span>{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-neo-danger text-neo-main p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black mb-3 flex items-center gap-2">
                  <FaUndo className="text-white drop-shadow-sm" />
                  When to REVERSE (Restart Medication)
                </h3>
                <ul className="space-y-2">
                  {taperPlan.reversal_criteria.map((criteria, index) => (
                    <li key={index} className="flex items-start font-bold gap-2">
                      <span className="mt-1 bg-white text-neo-danger rounded-full w-4 h-4 flex items-center justify-center text-xs border border-neo-main">!</span>
                      <span>{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-neo-card p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black text-neo-main mb-4 border-b-2 border-neo-main inline-block">Monitoring Schedule</h3>
                <div className="space-y-3">
                  {Object.entries(taperPlan.monitoring_schedule).map(([period, items], index) => (
                    <div key={index} className="bg-white p-4 rounded-md border-2 border-neo-main shadow-neo-pressed">
                      <p className="font-black text-neo-main mb-2 bg-neo-bg inline-block px-2 py-1 rounded border-2 border-neo-main text-sm">{period}</p>
                      <ul className="space-y-1">
                        {items.map((item, idx) => (
                          <li key={idx} className="text-sm text-neo-text font-medium flex items-start gap-2">
                            <span className="text-neo-accent font-bold">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black text-neo-main mb-4 flex items-center gap-2">
                  <FaCheckCircle className="text-neo-accent" />
                  Patient Education
                </h3>
                <div className="space-y-3">
                  {taperPlan.patient_education.map((education, index) => (
                    <div key={index} className="flex items-start gap-3 bg-neo-card p-4 rounded-md border-2 border-neo-main">
                      <span className="w-7 h-7 bg-neo-accent text-white border-2 border-neo-main
                                     rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-neo-sm">
                        {index + 1}
                      </span>
                      <p className="text-neo-text font-medium">{education}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-neo-bg p-4 rounded-lg border-2 border-neo-main shadow-neo-sm flex flex-wrap items-center justify-between gap-4">
                <span className="text-sm font-bold text-neo-main">Save this schedule for your records</span>
                <div className="flex gap-3">
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2.5 bg-neo-accent text-white border-2 border-neo-main rounded-md 
                             font-bold hover:bg-blue-600 transition-all duration-200
                             flex items-center gap-2 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                  >
                    <FaPrint />
                    Print
                  </button>
                  <button
                    onClick={() => {
                      const data = JSON.stringify(taperPlan, null, 2);
                      const blob = new Blob([data], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `taper-plan-${medication.name}.json`;
                      a.click();
                    }}
                    className="px-4 py-2.5 bg-neo-success text-neo-main border-2 border-neo-main rounded-md 
                             font-bold hover:bg-green-400 transition-all duration-200
                             flex items-center gap-2 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                  >
                    <FaDownload />
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white p-4 flex justify-between items-center border-t-2 border-neo-main">
          {isAiGenerated && (
            <span className="text-sm text-neo-text-sub font-bold flex items-center gap-2">
              <FaRobot className="text-neo-accent" />
              AI-generated | Verify with healthcare provider
            </span>
          )}
          {!isAiGenerated && <span></span>}
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-neo-main text-white border-2 border-neo-main rounded-md font-bold 
                     hover:bg-gray-800 transition-all duration-200 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaperModal;
